import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ticketsAPI } from '../api/client';
import type { MyTicket } from '../types';
import toast from 'react-hot-toast';
import { ScreenshotPicker, ScreenshotStrip } from '../components/TicketScreenshots';
import { pasteScreenshots } from '../lib/screenshots';
import { notifyBadgesStale } from '../lib/badges';
import { threadTime, ticketThread } from '../lib/ticketThread';

const LABEL_CLASSES: Record<MyTicket['status'], string> = {
  new: 'bg-gray-100 text-gray-600',
  needs_decision: 'bg-gold/10 text-gold',
  accepted: 'bg-navy/10 text-navy',
  in_progress: 'bg-navy/10 text-navy',
  on_staging: 'bg-blue-50 text-blue-700',
  shipped: 'bg-green-50 text-green-700',
  declined: 'bg-gray-100 text-gray-600',
  closed: 'bg-gray-100 text-gray-600',
};

export default function MyRequests() {
  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [followUp, setFollowUp] = useState<Record<number, string>>({});
  const [shots, setShots] = useState<Record<number, File[]>>({});
  const [sending, setSending] = useState<number | null>(null);

  const load = () => {
    ticketsAPI.mine()
      .then((rows) => {
        setTickets(rows);
        notifyBadgesStale();
      })
      .catch(() => toast.error('Could not load your requests.'));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-navy">Your requests</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Status and replies from the Iron Forums team. Staff notes stay private.
        </p>
      </div>
      {tickets.length === 0 && (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500 shadow-sm">
          Nothing sent yet. Use Contact support in <Link to="/settings" className="text-navy underline">Settings</Link>.
        </div>
      )}
      {tickets.map((ticket) => {
        const files = shots[ticket.id] || [];
        const setFiles = (next: File[]) => setShots((current) => ({ ...current, [ticket.id]: next }));
        const text = (followUp[ticket.id] || '').trim();
        return (
          <article key={ticket.id} className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-navy">{ticket.kind_display}</h2>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${LABEL_CLASSES[ticket.status]}`}>
                {ticket.status_label}
              </span>
            </div>
            <div className="space-y-3">
              {ticketThread(ticket.reports, ticket.member_replies).map((item) => (
                item.type === 'report' ? (
                  <div key={`report-${item.report.id}`} className="flex flex-col items-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy text-white px-3.5 py-2.5 space-y-2">
                      {item.report.logged_by_name && (
                        <p className="text-xs text-white/70">
                          Logged for you by {item.report.logged_by_name}
                          {item.report.logged_via_display ? ` from your ${item.report.logged_via_display.toLowerCase()}` : ''}
                        </p>
                      )}
                      {item.report.message && (
                        <p className="text-sm whitespace-pre-wrap">{item.report.message}</p>
                      )}
                      <ScreenshotStrip images={item.report.images || []} />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1 mr-1">You · {threadTime(item.at)}</p>
                  </div>
                ) : (
                  <div key={`reply-${item.reply.id}`} className="flex flex-col items-start">
                    <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 text-gray-800 px-3.5 py-2.5">
                      <p className="text-sm whitespace-pre-wrap">{item.reply.body}</p>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1 ml-1">Iron Forums · {threadTime(item.at)}</p>
                  </div>
                )
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={followUp[ticket.id] || ''}
                  onChange={(event) => setFollowUp((current) => ({ ...current, [ticket.id]: event.target.value }))}
                  onPaste={(event) => pasteScreenshots(event, files, setFiles)}
                  placeholder={ticket.closed ? 'This is still a problem' : 'Add a follow-up'}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  type="button"
                  disabled={sending === ticket.id || (!text && files.length === 0)}
                  onClick={async () => {
                    setSending(ticket.id);
                    try {
                      await ticketsAPI.followUp(ticket.id, text, files);
                      setFollowUp((current) => ({ ...current, [ticket.id]: '' }));
                      setFiles([]);
                      load();
                    } catch (err) {
                      const serverError = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
                      toast.error(serverError || 'Could not send that follow-up.');
                    } finally {
                      setSending(null);
                    }
                  }}
                  className="px-3 py-2 bg-navy text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {sending === ticket.id ? 'Sending…' : 'Send'}
                </button>
              </div>
              <ScreenshotPicker files={files} onChange={setFiles} disabled={sending === ticket.id} />
            </div>
          </article>
        );
      })}
    </div>
  );
}
